import { useEffect } from "react";

/**
 * Reusable SEO component for news pages.
 *
 * Writes <title>, meta tags, canonical, OpenGraph, Twitter Card, robots, and
 * JSON-LD into the document head. Because modern crawlers (Google, Bing, GPT,
 * Claude, Perplexity, Gemini) all execute JS, SPA-side updates are indexable.
 *
 * Renders NO markup itself — it's imperative-only.
 */

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "tag" || v === undefined || v === null) return;
    el.setAttribute(k, v);
  });
  return el;
};

const removeBySelector = (selector) => {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
};

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = String(html);
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

const clip = (text, max = 160) => {
  const s = stripHtml(text);
  if (s.length <= max) return s;
  return s.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
};

const Seo = ({
  title,
  description,
  canonical,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  type = "website",
  siteName,
  publishedTime,
  modifiedTime,
  author,
  category,
  tags,
  keywords,
  locale = "en_US",
  language = "en",
  twitterSite,
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  jsonLd,
}) => {
  useEffect(() => {
    const safeTitle = String(title || siteName || "News");
    const safeDesc = clip(description);
    const fullTitle =
      siteName && safeTitle !== siteName ? `${safeTitle} — ${siteName}` : safeTitle;
    const canonicalUrl =
      canonical || (typeof window !== "undefined" ? window.location.href : "");
    const ogImage = image || "";
    const finalImageAlt = imageAlt || (safeTitle && `${safeTitle}`);
    const isArticle = type === "article";

    document.title = fullTitle;

    // <html lang>
    document.documentElement.setAttribute("lang", language);

    // Description
    upsertMeta('meta[name="description"]', {
      tag: "meta",
      name: "description",
      content: safeDesc,
    });

    // Robots
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[name="googlebot"]', { name: "googlebot", content: robots });

    // Optional keywords
    if (keywords && keywords.length) {
      upsertMeta('meta[name="keywords"]', {
        name: "keywords",
        content: Array.isArray(keywords) ? keywords.join(", ") : String(keywords),
      });
    } else {
      removeBySelector('meta[name="keywords"]');
    }

    // Canonical
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonicalUrl);
    }

    // Open Graph
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: safeDesc });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: locale });
    if (canonicalUrl) upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    if (siteName) upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteName });
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
      upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: ogImage });
      upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: finalImageAlt });
      if (imageWidth) upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: String(imageWidth) });
      if (imageHeight) upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: String(imageHeight) });
    } else {
      removeBySelector('meta[property^="og:image"]');
    }

    // Article-specific OG
    if (isArticle) {
      if (publishedTime) upsertMeta('meta[property="article:published_time"]', { property: "article:published_time", content: publishedTime });
      if (modifiedTime) upsertMeta('meta[property="article:modified_time"]', { property: "article:modified_time", content: modifiedTime });
      if (author) upsertMeta('meta[property="article:author"]', { property: "article:author", content: author });
      if (category) upsertMeta('meta[property="article:section"]', { property: "article:section", content: category });
      // article:tag can be repeated → wipe & re-add
      removeBySelector('meta[property="article:tag"]');
      (tags || []).forEach((t) => {
        const m = document.createElement("meta");
        m.setAttribute("property", "article:tag");
        m.setAttribute("content", String(t));
        document.head.appendChild(m);
      });
    } else {
      removeBySelector('meta[property^="article:"]');
    }

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: ogImage ? "summary_large_image" : "summary" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: safeDesc });
    if (ogImage) {
      upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
      upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: finalImageAlt });
    }
    if (twitterSite) {
      upsertMeta('meta[name="twitter:site"]', { name: "twitter:site", content: twitterSite });
      upsertMeta('meta[name="twitter:creator"]', { name: "twitter:creator", content: twitterSite });
    }

    // JSON-LD — wipe previous and inject fresh
    removeBySelector('script[data-seo="ld"]');
    if (jsonLd) {
      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.setAttribute("data-seo", "ld");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [
    title,
    description,
    canonical,
    image,
    imageAlt,
    imageWidth,
    imageHeight,
    type,
    siteName,
    publishedTime,
    modifiedTime,
    author,
    category,
    JSON.stringify(tags || []),
    JSON.stringify(keywords || []),
    locale,
    language,
    twitterSite,
    robots,
    JSON.stringify(jsonLd || null),
  ]);

  return null;
};

/* ---------- JSON-LD helpers ---------- */

export const organizationLd = ({ siteName, url, logoUrl, description, sameAs }) => ({
  "@context": "https://schema.org",
  "@type": "NewsMediaOrganization",
  name: siteName,
  url,
  logo: {
    "@type": "ImageObject",
    url: logoUrl,
    width: 512,
    height: 512,
  },
  description,
  sameAs: Array.isArray(sameAs) && sameAs.length ? sameAs : undefined,
});

export const webSiteLd = ({ siteName, url, description, language = "en" }) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: siteName,
  url,
  description,
  inLanguage: language,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${url.replace(/\/$/, "")}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

export const newsArticleLd = ({
  headline,
  description,
  image,
  imageWidth,
  imageHeight,
  datePublished,
  dateModified,
  authorName,
  authorUrl,
  siteName,
  siteUrl,
  logoUrl,
  articleUrl,
  category,
  keywords,
  articleBody,
  wordCount,
  language = "en",
}) => {
  const imgObj = image
    ? {
        "@type": "ImageObject",
        url: image,
        width: imageWidth || 1200,
        height: imageHeight || 630,
      }
    : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    headline: headline?.slice(0, 110),
    description,
    image: imgObj ? [imgObj] : undefined,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      "@type": "Person",
      name: authorName || "Editorial Team",
      url: authorUrl,
    },
    publisher: {
      "@type": "NewsMediaOrganization",
      name: siteName,
      url: siteUrl,
      logo: logoUrl
        ? { "@type": "ImageObject", url: logoUrl, width: 512, height: 512 }
        : undefined,
    },
    articleSection: category,
    keywords: Array.isArray(keywords) && keywords.length ? keywords.join(", ") : undefined,
    articleBody,
    wordCount,
    inLanguage: language,
    isAccessibleForFree: true,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "[data-speakable]"],
    },
  };
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export const collectionPageLd = ({ name, description, url, items, siteName, siteUrl }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description,
  url,
  isPartOf: { "@type": "WebSite", name: siteName, url: siteUrl },
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: it.url,
      name: it.name,
    })),
  },
});

/* Utility helpers callers can use */
export const stripHtmlToText = stripHtml;
export const clipText = clip;

export default Seo;
